import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { UseraccessserviceService } from './useraccessservice.service';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'impata.theme'; // also read by the inline script in index.html
const INK = '#1f3038';
const WHITE = '#ffffff';
const DARK_PAGE = '#1f3038';  // --surface in dark: where brand text usually sits
const LIGHT_PAGE = '#ffffff';
const MIN_TEXT_CONTRAST = 4.5; // WCAG AA for normal text

/**
 * Light / dark theme plus school-colour contrast protection.
 *
 * Theme: the user's choice is kept in localStorage; with no choice the device setting is
 * followed (and followed live if it changes).
 *
 * Contrast: a school can set any primary colour, in a custom stylesheet loaded per page. After
 * styles change (theme switch, navigation, a stylesheet finishing loading) this measures the
 * colours actually in effect and writes, inline on <html>:
 *   --on-brand    white or ink, whichever is readable on the brand colour
 *   --brand-text  the brand colour nudged lighter/darker until it reads as text on the page
 *   --subColor    button text, only when the school's own pair falls below 4.5:1
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly media = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  readonly theme$ = new BehaviorSubject<Theme>(this.readAttribute());
  private choice: ThemeChoice = 'system';
  private probe: HTMLElement;

  constructor(private router: Router, private zone: NgZone, private http: HttpClient, private access: UseraccessserviceService) {
    this.choice = this.readChoice();
    this.apply(false);

    this.media?.addEventListener?.('change', () => {
      if (this.choice === 'system') this.zone.run(() => this.apply(true));
    });

    // custom school stylesheets are swapped per route; re-measure once things settle
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.scheduleContrast());
    this.watchStylesheets();
    this.syncFromAccount();
  }

  // ---------- account sync ----------
  // The browser copy (localStorage) is applied first so pages never flash; the account value
  // then follows the user to other browsers and devices.

  private get signedIn(): boolean {
    try { return this.access.loggedIn(); } catch { return false; }
  }

  /** Apply the account's saved choice, if any. Skipped offline or signed out; failures are ignored. */
  private syncFromAccount() {
    if (!this.signedIn || !navigator.onLine) return;
    this.http.get<any>(environment.ImpataAppAPIRoot + 'useraccount/themepreference').subscribe({
      next: res => {
        const theme = res?.theme;
        if ((theme === 'light' || theme === 'dark' || theme === 'system') && theme !== this.choice) {
          this.setChoice(theme, false);
        }
      },
      error: () => { /* column not deployed yet, or offline: keep the browser's choice */ }
    });
  }

  private saveToAccount(choice: ThemeChoice) {
    if (!this.signedIn) return;
    this.http.post(environment.ImpataAppAPIRoot + 'useraccount/themepreference', { theme: choice })
      .subscribe({ error: () => { /* the browser copy still holds the choice */ } });
  }

  get current(): Theme {
    return this.theme$.value;
  }

  get currentChoice(): ThemeChoice {
    return this.choice;
  }

  toggle() {
    this.setChoice(this.current === 'dark' ? 'light' : 'dark');
  }

  setChoice(choice: ThemeChoice, saveToAccount = true) {
    this.choice = choice;
    try {
      if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, choice);
    } catch { /* private mode: the choice still applies for this visit */ }
    this.apply(true);
    if (saveToAccount) this.saveToAccount(choice);
  }

  private apply(animate: boolean) {
    const theme: Theme = this.choice === 'system'
      ? (this.media?.matches ? 'dark' : 'light')
      : this.choice;
    const root = document.documentElement;
    if (animate) {
      root.classList.add('theme-animating');
      setTimeout(() => root.classList.remove('theme-animating'), 350);
    }
    root.setAttribute('data-theme', theme);
    this.theme$.next(theme);
    this.scheduleContrast();
  }

  // ---------- contrast ----------

  private contrastTimer: any;

  private scheduleContrast() {
    clearTimeout(this.contrastTimer);
    this.contrastTimer = setTimeout(() => this.refreshContrast(), 60);
  }

  /** Re-run when any stylesheet is added to <head> or finishes loading. */
  private watchStylesheets() {
    const onLoad = () => this.scheduleContrast();
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          if (node instanceof HTMLLinkElement || node instanceof HTMLStyleElement) {
            node.addEventListener('load', onLoad, { once: true });
            onLoad();
          }
        });
        if (m.removedNodes.length) onLoad();
      }
    });
    observer.observe(document.head, { childList: true });
    // school stylesheets are rendered by app.component's template, not in <head>; 'load'
    // does not bubble, so listen in the capture phase for any stylesheet finishing anywhere
    document.addEventListener('load', (e) => {
      if (e.target instanceof HTMLLinkElement && e.target.rel === 'stylesheet') onLoad();
    }, true);
  }

  refreshContrast() {
    const root = document.documentElement;
    // clear what we wrote last time so we measure the school's own colours
    ['--on-brand', '--brand-text', '--subColor'].forEach(p => root.style.removeProperty(p));
    const style = getComputedStyle(root);

    const brand = this.toRgb(style.getPropertyValue('--primary-darker'));
    if (brand) {
      root.style.setProperty('--on-brand', this.bestOn(brand));
      const page = this.toRgb(this.current === 'dark' ? DARK_PAGE : LIGHT_PAGE);
      const text = this.current === 'dark' ? this.mintOrAdjusted(brand, page) : this.adjustUntilReadable(brand, page);
      root.style.setProperty('--brand-text', this.toHex(text));
    }

    // button colours: keep the school's text colour unless it would be unreadable
    const btnBg = this.toRgb(style.getPropertyValue('--themeColor'));
    const btnText = this.toRgb(style.getPropertyValue('--subColor'));
    if (btnBg && btnText && this.contrast(btnBg, btnText) < MIN_TEXT_CONTRAST) {
      root.style.setProperty('--subColor', this.bestOn(btnBg));
    }
  }

  /** In dark mode, the school colour is used for text only when it reads well; else mint. */
  private mintOrAdjusted(brand: number[], page: number[]): number[] {
    const adjusted = this.adjustUntilReadable(brand, page);
    return adjusted ?? this.toRgb('#6fd4b8');
  }

  /** Mixes towards white (dark page) or black (light page) until text contrast is met. */
  private adjustUntilReadable(color: number[], page: number[]): number[] {
    if (this.contrast(color, page) >= MIN_TEXT_CONTRAST) return color;
    const towards = this.luminance(page) < 0.5 ? [255, 255, 255] : [0, 0, 0];
    for (let t = 0.05; t <= 1; t += 0.05) {
      const mixed = color.map((c, i) => Math.round(c + (towards[i] - c) * t));
      if (this.contrast(mixed, page) >= MIN_TEXT_CONTRAST) return mixed;
    }
    return towards;
  }

  private bestOn(bg: number[]): string {
    return this.contrast(bg, this.toRgb(WHITE)) >= this.contrast(bg, this.toRgb(INK)) ? WHITE : INK;
  }

  // ---------- colour maths (WCAG relative luminance) ----------

  private luminance([r, g, b]: number[]): number {
    const f = (c: number) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  }

  contrast(a: number[], b: number[]): number {
    const [l1, l2] = [this.luminance(a), this.luminance(b)].sort((x, y) => y - x);
    return (l1 + 0.05) / (l2 + 0.05);
  }

  /** Any CSS colour (hex, rgb, named, var already resolved) to [r, g, b]; null if unusable. */
  private toRgb(value: string): number[] | null {
    const v = (value || '').trim();
    if (!v) return null;
    if (!this.probe) {
      this.probe = document.createElement('span');
      this.probe.style.display = 'none';
      document.body.appendChild(this.probe);
    }
    this.probe.style.color = '';
    this.probe.style.color = v;
    if (!this.probe.style.color) return null;
    const m = getComputedStyle(this.probe).color.match(/\d+(\.\d+)?/g);
    return m && m.length >= 3 ? m.slice(0, 3).map(Number) : null;
  }

  private toHex(rgb: number[]): string {
    return '#' + rgb.map(c => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('');
  }

  private readChoice(): ThemeChoice {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'dark' || saved === 'light' ? saved : 'system';
    } catch {
      return 'system';
    }
  }

  private readAttribute(): Theme {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }
}
